// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import type { RouterHistory } from 'react-router';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import ShipmentsTable from './ShipmentsTable';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  onCreateNew: () => void,
  shipments: Object,
  deleteEnabled: boolean,
  editEnabled: boolean,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedShipmentsContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const {
      history,
      onCreateNew,
      shipments,
      relay,
      filtersDirty,
      editEnabled,
      deleteEnabled,
    } = this.props;

    if (
      (!shipments.shipments.edges || !shipments.shipments.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <ShipmentsTable
          history={history}
          onCreateNew={onCreateNew}
          shipmentEdges={shipments.shipments.edges}
          deleteEnabled={deleteEnabled}
          editEnabled={editEnabled}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedShipmentsContainer,
  {
    shipments: graphql`
      fragment PaginatedShipmentsContainer_shipments on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "ShipmentSort" }
          statuses: { type: "[ShipmentStatus!]" }
          customerIds: { type: "[ID!]" }
          orderIds: { type: "[ID!]" }
          estimatedShipDateStart: { type: "Date" }
          estimatedShipDateEnd: { type: "Date" }
          actualShipDateStart: { type: "Date" }
          actualShipDateEnd: { type: "Date" }
        ) {
        shipments(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          statuses: $statuses
          customerIds: $customerIds
          orderIds: $orderIds
          estimatedShipDateStart: $estimatedShipDateStart
          estimatedShipDateEnd: $estimatedShipDateEnd
          actualShipDateStart: $actualShipDateStart
          actualShipDateEnd: $actualShipDateEnd
        ) @connection(key: "PaginatedShipmentsContainer_shipments", filters: []) {
          edges {
            cursor
            ...ShipmentsTable_shipmentEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedShipmentsContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: ShipmentSort
        $statuses: [ShipmentStatus!]
        $customerIds: [ID!]
        $orderIds: [ID!]
        $estimatedShipDateStart: Date
        $estimatedShipDateEnd: Date
        $actualShipDateStart: Date
        $actualShipDateEnd: Date
      ) {
        ...PaginatedShipmentsContainer_shipments
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            statuses: $statuses
            customerIds: $customerIds
            orderIds: $orderIds
            estimatedShipDateStart: $estimatedShipDateStart
            estimatedShipDateEnd: $estimatedShipDateEnd
            actualShipDateStart: $actualShipDateStart
            actualShipDateEnd: $actualShipDateEnd
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
